import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  titleWithVersion,
  versionLabel,
  type PolicyAcceptanceMatrix,
  type PolicyAcceptanceStatus,
  type PolicyRemindResult,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  EmptyState,
  LoadingPane,
  PageHeader,
  Select,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { RecordAcceptanceDialog } from './RecordAcceptanceDialog.js';
import { matrixKey, policiesKey, reasonLabel } from './shared.js';
import '../training/training.css';
import './policies.css';

const ALL = '__all__';

/** Tooltip / screen-reader text for one cell. */
function cellText(s: PolicyAcceptanceStatus, reacceptIntervalMonths: number | null): string {
  if (s.status === 'accepted') {
    return `${s.method === 'recorded' ? 'Recorded acceptance' : 'Accepted'} ${formatDate(s.acceptedAt)} (${versionLabel(
      s.acceptedDocumentVersion ?? '',
      s.acceptedVersion ?? 0,
    )})${s.renewBy ? ` · reconfirm by ${formatDate(s.renewBy)}` : ''}`;
  }
  const why = reasonLabel(s.reason, reacceptIntervalMonths);
  return `${s.status === 'overdue' ? 'Overdue' : 'Outstanding'} — due ${formatDate(s.dueOn)}${why ? ` (${why.toLowerCase()})` : ''}`;
}

/** Member × policy acceptance grid for admins and reviewers. */
export function PolicyTrackingPage() {
  const { me } = useAuth();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin' || (me?.user.isSystemAdmin ?? false);
  const canView = isAdmin || org.role === 'reviewer';

  const matrix = useQuery({
    queryKey: matrixKey(org.orgId),
    queryFn: () => apiFetch<PolicyAcceptanceMatrix>(`/orgs/${org.orgId}/policy-acceptance`),
    enabled: canView,
  });

  const [onlyOutstanding, setOnlyOutstanding] = useState(false);
  const [policyFilter, setPolicyFilter] = useState(ALL);
  const [recordOpen, setRecordOpen] = useState(false);

  const remind = useMutation({
    mutationFn: () =>
      apiFetch<PolicyRemindResult>(`/orgs/${org.orgId}/policy-acceptance/remind`, {
        method: 'POST',
        body: policyFilter === ALL ? {} : { corpusItemId: policyFilter },
      }),
    onSuccess: (result) => {
      toast(
        result.recipients === 0
          ? 'Nobody has anything outstanding — no reminders sent.'
          : `Reminder sent to ${result.recipients} ${result.recipients === 1 ? 'person' : 'people'}.`,
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
    },
    onError: (err) =>
      toast(
        err instanceof ApiRequestError && err.code === 'rate_limited'
          ? 'Reminders are limited to three an hour — please try again later.'
          : err instanceof ApiRequestError
            ? err.message
            : 'Could not send reminders',
        'error',
      ),
  });

  const view = useMemo(() => {
    if (!matrix.data) return null;
    const policies =
      policyFilter === ALL
        ? matrix.data.policies
        : matrix.data.policies.filter((p) => p.corpusItemId === policyFilter);
    const keep = new Set(policies.map((p) => p.releaseId));
    const members = matrix.data.members
      .map((m) => ({ ...m, policies: m.policies.filter((s) => keep.has(s.releaseId)) }))
      .filter((m) => !onlyOutstanding || m.policies.some((s) => s.status !== 'accepted'));
    const counts = { accepted: 0, outstanding: 0, overdue: 0, total: 0 };
    for (const m of members) {
      for (const s of m.policies) {
        counts[s.status] += 1;
        counts.total += 1;
      }
    }
    return { policies, members, counts };
  }, [matrix.data, policyFilter, onlyOutstanding]);

  if (!canView) {
    return (
      <EmptyState
        title="Not available"
        message="Policy acceptance tracking is available to organisation admins and reviewers."
      />
    );
  }
  if (matrix.isLoading) return <LoadingPane label="Loading policy acceptance" />;
  if (matrix.isError || !matrix.data || !view) {
    return (
      <div>
        <PageHeader title="Policy acceptance" />
        <EmptyState
          title="Could not load policy acceptance"
          message="Something went wrong fetching the organisation's acceptance status — please try again shortly."
        />
      </div>
    );
  }
  const all = matrix.data;
  const percent = all.counts.total === 0 ? 0 : Math.round((all.counts.accepted / all.counts.total) * 100);

  return (
    <div>
      <PageHeader
        title="Policy acceptance"
        subtitle="Who has accepted which published policy version. Recorded acceptances (paper forms, previous platforms) are marked A."
        actions={
          <>
            <a href={apiUrl(`/orgs/${org.orgId}/policy-acceptance/export.xlsx`)}>
              <Button variant="secondary">Download Excel</Button>
            </a>
            {isAdmin && (
              <Button
                variant="secondary"
                loading={remind.isPending}
                disabled={all.policies.length === 0}
                onClick={() => remind.mutate()}
              >
                Remind outstanding
              </Button>
            )}
            {isAdmin && (
              <Button disabled={all.policies.length === 0} onClick={() => setRecordOpen(true)}>
                Record acceptance
              </Button>
            )}
          </>
        }
      />

      {all.policies.length === 0 ? (
        <EmptyState
          title="No policies published for acceptance"
          message="Open a corpus item and choose “Publish for acceptance” to start tracking who has read and accepted it."
        />
      ) : (
        <>
          <div className="policy-summary" aria-label="Acceptance summary">
            <Badge tone="success">{percent}% accepted</Badge>
            {all.counts.overdue > 0 && <Badge tone="danger">{all.counts.overdue} overdue</Badge>}
            {all.counts.outstanding > 0 && <Badge tone="warning">{all.counts.outstanding} outstanding</Badge>}
            <Badge tone="neutral">
              {all.members.length} {all.members.length === 1 ? 'person' : 'people'} · {all.policies.length}{' '}
              {all.policies.length === 1 ? 'policy' : 'policies'}
            </Badge>
          </div>
          <div className="policy-filters">
            <label>
              <input
                type="checkbox"
                checked={onlyOutstanding}
                onChange={(e) => setOnlyOutstanding(e.target.checked)}
              />
              Only people with outstanding items
            </label>
            <label>
              <span className="sr-only">Policy</span>
              <Select value={policyFilter} onChange={(e) => setPolicyFilter(e.target.value)}>
                <option value={ALL}>All policies</option>
                {all.policies.map((p) => (
                  <option key={p.releaseId} value={p.corpusItemId}>
                    {titleWithVersion(p.title, p.documentVersion, p.versionNumber)}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {view.members.length === 0 ? (
            <EmptyState
              title={onlyOutstanding ? 'Everyone is up to date' : 'No members'}
              message={
                onlyOutstanding
                  ? 'Nobody has an outstanding or overdue policy for this selection.'
                  : 'Invite members to start tracking acceptance.'
              }
            />
          ) : (
            <div className="track-table-wrap">
              <table className="track-table">
                <thead>
                  <tr>
                    {/* Sticky header cells paint in DOM order — descending
                        z-index keeps each rotated title above the cells it
                        crosses (see TrackTrainingPage). */}
                    <th scope="col" className="track-member-col" style={{ zIndex: view.policies.length + 3 }}>
                      Member
                    </th>
                    {view.policies.map((p, i) => (
                      <th
                        key={p.releaseId}
                        scope="col"
                        className="track-course-head"
                        title={titleWithVersion(p.title, p.documentVersion, p.versionNumber)}
                        style={{ zIndex: view.policies.length + 1 - i }}
                      >
                        <span className="track-course-title">
                          {titleWithVersion(p.title, p.documentVersion, p.versionNumber)}
                        </span>
                      </th>
                    ))}
                    <th scope="col" style={{ verticalAlign: 'bottom', zIndex: 1 }}>
                      Accepted
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {view.members.map((member) => {
                    const accepted = member.policies.filter((s) => s.status === 'accepted').length;
                    return (
                      <tr key={member.userId}>
                        <th scope="row" className="track-member-col">
                          <div className="track-member-name">{member.name || member.email}</div>
                          <div className="track-member-role">
                            {member.role} · joined {formatDate(member.joinedAt)}
                          </div>
                        </th>
                        {member.policies.map((s) => {
                          const policy = view.policies.find((p) => p.releaseId === s.releaseId);
                          const text = cellText(s, policy?.reacceptIntervalMonths ?? null);
                          const recorded = s.method === 'recorded';
                          return (
                            <td key={s.releaseId}>
                              <span
                                className={`track-cell ${
                                  s.status === 'accepted'
                                    ? `track-done${recorded ? ' track-attested' : ''}`
                                    : s.status === 'overdue'
                                      ? 'track-overdue'
                                      : 'track-outstanding'
                                }`}
                                title={text}
                              >
                                <span aria-hidden="true">
                                  {s.status === 'accepted' ? (recorded ? 'A' : '✓') : s.status === 'overdue' ? '!' : '–'}
                                </span>
                                <span className="sr-only">{text}</span>
                              </span>
                              {s.status === 'accepted' ? (
                                <span className="policy-cell-version" aria-hidden="true">
                                  {versionLabel(s.acceptedDocumentVersion ?? '', s.acceptedVersion ?? 0)}
                                </span>
                              ) : (
                                <span className="policy-cell-version" aria-hidden="true">
                                  {s.dueOn?.slice(5)}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td>
                          <strong>
                            {accepted}/{member.policies.length}
                          </strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="track-legend">
            <span className="track-cell track-done">✓</span> accepted (version shown) ·{' '}
            <span className="track-cell track-done track-attested">A</span> recorded acceptance ·{' '}
            <span className="track-cell track-outstanding">–</span> outstanding (due date shown) ·{' '}
            <span className="track-cell track-overdue">!</span> overdue
          </p>
        </>
      )}

      {isAdmin && (
        <RecordAcceptanceDialog
          policies={all.policies}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
        />
      )}
    </div>
  );
}
